import { enhancedApi } from '@store/generated/waiting-room';

const WAITING_ROOM_TAG_TYPES = ['WaitingRoomLink', 'WaitingRoomQueue'] as const;

/**
 * Response shape for the GDN-1538 explicit end-session endpoint.
 * Kept in sync with `WaitingRoomEndSessionResponse` (Pydantic).
 *
 * Defined inline rather than imported from generated/ because the
 * endpoint is wired via `injectEndpoints` below so the FE can call it
 * without needing a fresh codegen pass against the new OpenAPI schema.
 */
export interface WaitingRoomEndSessionResponse {
  encounter_id: string;
  room_deleted: boolean;
  queue_entry_ids: string[];
  participants_at_end: number;
  duration_seconds: number | null;
}

export const waitingRoomApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: WAITING_ROOM_TAG_TYPES,
    endpoints: {
      getV2ProviderWaitingRoomLink: {
        providesTags: [{ type: 'WaitingRoomLink', id: 'CURRENT' }],
      },
      getV2ProviderWaitingRoomQueue: {
        providesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      putV2ProviderWaitingRoomAvailability: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      postV2ProviderWaitingRoomQueueByEntryIdAdmit: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      postV2ProviderWaitingRoomEncountersByEncounterIdJoin: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
      deleteV2ProviderWaitingRoomQueueByEntryId: {
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // GDN-1538 end-session.
      // Injected (not codegen'd) so the FE can ship without waiting for
      // a fresh OpenAPI pull. The mutation is idempotent and the
      // backend route is `POST /v2/provider/waiting-room/encounters/{id}/end`.
      endWaitingRoomSession: builder.mutation<
        WaitingRoomEndSessionResponse,
        { encounterId: string }
      >({
        query: ({ encounterId }) => ({
          url: `/v2/provider/waiting-room/encounters/${encounterId}/end`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'WaitingRoomQueue', id: 'LIST' }],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetV2ProviderWaitingRoomLinkQuery: useGetProviderWaitingRoomLinkQuery,
  useGetV2ProviderWaitingRoomQueueQuery: useGetProviderWaitingRoomQueueQuery,
  usePutV2ProviderWaitingRoomAvailabilityMutation: useSetProviderWaitingRoomAvailabilityMutation,
  usePostV2ProviderWaitingRoomHeartbeatMutation: useProviderWaitingRoomHeartbeatMutation,
  usePostV2ProviderWaitingRoomQueueByEntryIdAdmitMutation: useAdmitWaitingRoomPatientMutation,
  usePostV2ProviderWaitingRoomEncountersByEncounterIdJoinMutation:
    useJoinWaitingRoomEncounterMutation,
  useDeleteV2ProviderWaitingRoomQueueByEntryIdMutation: useRemoveWaitingRoomPatientMutation,
  useEndWaitingRoomSessionMutation,
} = waitingRoomApi;
